import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { CharacterVisual } from "@/components/CharacterVisual";

export default function PublicResult() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [image, setImage] = useState(null);

  const fetchImage = useCallback(async (path) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const reader = new FileReader();
      reader.onloadend = () => setImage(reader.result);
      reader.readAsDataURL(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    api.get(`/results/${publicId}/public`).then(({ data }) => {
      setData(data);
      if (data.character_image_url) fetchImage(data.character_image_url);
    }).catch(() => setNotFound(true));
  }, [publicId, fetchImage]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <p className="font-display text-4xl gold-text mb-4">Hasil nggak ketemu</p>
          <button onClick={() => navigate("/")} className="gold-btn rounded-full px-6 py-3">Coba Ruang Kendali</button>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center"><div className="w-14 h-14 rounded-full border-2 border-amber-400/30 border-t-amber-300 animate-spin" /></div>;

  return (
    <div className="relative min-h-screen w-full flex items-center">
      <div className="relative z-10 max-w-lg mx-auto px-5 sm:px-8 py-14 text-center w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="font-display text-lg tracking-[0.2em] mb-6">RUANG <span className="gold-text">KENDALI</span></div>
          <p className="text-sm text-gray-400 mb-5">Seseorang dapet karakter…</p>

          <div className="rounded-3xl overflow-hidden border border-amber-400/20 aspect-[3/4] max-w-xs mx-auto mb-6">
            <CharacterVisual icon={data.character.icon} name={data.character.name}
              imageUrl={data.character_image_url} image={image} status={data.image_status}
              category={data.category} className="w-full h-full" />
          </div>

          <h1 className="font-display text-5xl sm:text-6xl font-bold gold-text leading-none">{data.character.name}</h1>
          <div className="mt-3 font-mono text-xl text-amber-300">{data.compatibility}% KESESUAIAN</div>
          {data.character.rarity === "rare" && (
            <div className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-300/60 bg-amber-400/10 text-amber-200 text-xs font-semibold tracking-[0.2em] uppercase">
              <Sparkles size={13} /> Karakter Langka
            </div>
          )}
          <p className="mt-4 text-lg text-gray-300 italic">"{data.share_tagline}"</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10 glass rounded-3xl p-7">
          <div className="flex justify-center mb-3 text-amber-300"><Sparkles size={22} /></div>
          <p className="font-display text-2xl sm:text-3xl leading-snug">Penasaran karakter <span className="gold-text">lo</span> apa?</p>
          <button onClick={() => navigate("/")} data-testid="public-cta-button"
            className="mt-6 w-full gold-btn rounded-full px-8 py-4 inline-flex items-center justify-center gap-3">
            COBA RUANG KENDALI <span className="opacity-70">— GRATIS</span> <ArrowRight size={18} />
          </button>
          <p className="mt-3 text-xs text-gray-500">Pikir. Hitung. Putuskan.</p>
        </motion.div>
      </div>
    </div>
  );
}
